import React, { useState, useEffect, useRef, useContext } from 'react';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import { UserContext } from '../context/UserContext';
import apiConfig from '../config/apiConfig';
import './Chat.css';

const Chat = () => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  const { user } = useContext(UserContext);
  const eventSourceRef = useRef(null);
  const isAtBottomRef = useRef(true);
  
  // Format message to handle /me commands
  const formatMessage = (message, username) => {
    if (message.startsWith('/me ')) {
      return {
        isAction: true,
        content: message.substring(4)
      };
    }
    return {
      isAction: false,
      content: message
    };
  };

  // Format timestamp to readable format
  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  // Scroll to bottom when messages change
  useEffect(() => {
    if (isAtBottomRef.current && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Track scroll position to determine if we should auto-scroll
  const handleScroll = () => {
    if (chatContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = chatContainerRef.current;
      // Consider user at bottom if they're within 100px of the bottom
      isAtBottomRef.current = scrollHeight - scrollTop - clientHeight < 100;
    }
  };

  // Load chat history and connect to SSE feed
  useEffect(() => {
    const fetchChatHistory = async () => {
      try {
        setIsLoading(true);
        const response = await fetch(apiConfig.shoutboxHistory, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          },
          credentials: 'include'
        });
        
        if (!response.ok) {
          throw new Error('Failed to fetch chat history');
        }
        
        const data = await response.json();
        setMessages(data);
      } catch (err) {
        setError('Error loading chat history: ' + err.message);
        console.error('Chat history error:', err);
      } finally {
        setIsLoading(false);
      }
    };

    const connectToSSE = () => {
      try {
        // Close any existing connection first
        if (eventSourceRef.current) {
          eventSourceRef.current.close();
        }
        
        eventSourceRef.current = new EventSource(apiConfig.shoutboxFeed, {
          withCredentials: true
        });
        
        eventSourceRef.current.onmessage = (event) => {
          const newMsg = JSON.parse(event.data);
          setMessages(prevMessages => [...prevMessages, newMsg]);
        };
        
        eventSourceRef.current.onerror = (error) => {
          console.error('SSE connection error:', error);
          // Try to reconnect after a delay if we're still mounted
          setTimeout(connectToSSE, 5000);
        };
      } catch (err) {
        setError('Error connecting to chat feed: ' + err.message);
        console.error('SSE connection setup error:', err);
      }
    };

    fetchChatHistory().then(() => {
      connectToSSE();
    });

    // Cleanup function to close the SSE connection when component unmounts
    return () => {
      if (eventSourceRef.current) {
        console.log('Closing chat SSE connection');
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    };
  }, []);

  const sendMessage = async (e) => {
    e.preventDefault();
    
    if (!newMessage.trim()) return;
    
    try {
      const response = await fetch(apiConfig.shoutboxMessage, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ message: newMessage })
      });
      
      if (!response.ok) {
        throw new Error('Failed to send message');
      }
      
      // Clear the input field after successful send
      setNewMessage('');
      // Force scroll to bottom after sending a message
      isAtBottomRef.current = true;
    } catch (err) {
      setError('Error sending message: ' + err.message);
      console.error('Message send error:', err);
    }
  };

  // Insert Markdown syntax based on the type
  const insertMarkdown = (syntax) => {
    const textArea = document.getElementById('chat-input');
    const start = textArea.selectionStart;
    const end = textArea.selectionEnd;
    const selectedText = newMessage.substring(start, end);
    let insertText = '';
    
    switch (syntax) {
      case 'bold':
        insertText = `**${selectedText}**`;
        break;
      case 'italic':
        insertText = `*${selectedText}*`;
        break;
      case 'code':
        insertText = `\`${selectedText}\``;
        break;
      case 'color':
        const color = prompt("Enter a color (e.g., red or #FF0000):", "red");
        if (!color) return;
        insertText = `[color=${color}]${selectedText}[/color]`;
        break;
      case 'me':
        setNewMessage('/me ' + newMessage);
        textArea.focus();
        return;
      default:
        return;
    }
    
    const newText = newMessage.substring(0, start) + insertText + newMessage.substring(end);
    setNewMessage(newText);
    
    setTimeout(() => {
      textArea.focus();
      textArea.selectionStart = start + insertText.length;
      textArea.selectionEnd = start + insertText.length;
    }, 0);
  };

  // Format message content to handle color tags and preserve line breaks
  const formatMessageContent = (content) => {
    // First, replace color tags with HTML spans
    const colorFormatted = content.replace(
      /\[color=([#a-zA-Z0-9]+)\](.*?)\[\/color\]/g,
      '<span style="color:$1">$2</span>'
    );
    
    // Return the formatted content with line breaks preserved
    return colorFormatted;
  };

  // Handle text area resize
  const handleTextAreaResize = (e) => {
    const textArea = e.target;
    textArea.style.height = 'auto'; // Reset height to recalculate
    textArea.style.height = `${textArea.scrollHeight}px`; // Set new height based on content
  };

  return (
    <div className="page-content">
      <h2>Global Chat</h2>
      
      {error && <div className="error-message">{error}</div>}
      
      <div 
        className="chat-container" 
        ref={chatContainerRef} 
        onScroll={handleScroll}
      >
        {isLoading ? (
          <div className="chat-loading">Loading chat history...</div>
        ) : messages.length > 0 ? (
          <div className="chat-messages">
            {messages.map((msg, index) => {
              const formattedMsg = formatMessage(msg.message, msg.username);
              return (
                <div key={index} className="chat-message">
                  <div className="message-timestamp">
                    {formatTimestamp(msg.time)}
                  </div>
                  <div className="message-content">
                    {formattedMsg.isAction ? (
                      <>
                        <Link 
                          to={`/character/view/${msg.playerId}`} 
                          className="username action-username"
                        >
                          {msg.username}
                        </Link>{' '}
                        <span className="action-text">
                          <ReactMarkdown rehypePlugins={[rehypeRaw]}>
                            {formatMessageContent(formattedMsg.content)}
                          </ReactMarkdown>
                        </span>
                      </>
                    ) : (
                      <>
                        <Link 
                          to={`/character/view/${msg.playerId}`}
                          className="username"
                        >
                          {msg.username}:
                        </Link>{' '}
                        <span className="message-text">
                          <ReactMarkdown
                            rehypePlugins={[rehypeRaw]}
                            components={{
                              // This custom component ensures newlines are preserved in paragraphs
                              p: ({node, ...props}) => (
                                <p style={{whiteSpace: 'pre-wrap'}} {...props} />
                              )
                            }}
                          >
                            {formatMessageContent(formattedMsg.content)}
                          </ReactMarkdown>
                        </span>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </div>
        ) : (
          <div className="no-messages">No messages yet. Be the first to say something!</div>
        )}
      </div>
      
      <div className="chat-input-area">
        <div className="markdown-toolbar">
          <button type="button" onClick={() => insertMarkdown('bold')} title="Bold">
            <strong>B</strong>
          </button>
          <button type="button" onClick={() => insertMarkdown('italic')} title="Italic">
            <em>I</em>
          </button>
          <button type="button" onClick={() => insertMarkdown('code')} title="Code">
            <code>Code</code>
          </button>
          <button type="button" onClick={() => insertMarkdown('color')} title="Color">
            <span style={{background: 'linear-gradient(90deg, #ff0000, #00ff00, #0000ff)', WebkitBackgroundClip: 'text', color: 'transparent'}}>
              Color
            </span>
          </button>
          <button type="button" onClick={() => insertMarkdown('me')} title="Action">
            /me
          </button>
        </div>
        
        <form onSubmit={sendMessage} className="message-form">
          <textarea
            id="chat-input"
            value={newMessage}
            onChange={(e) => {
              setNewMessage(e.target.value);
              handleTextAreaResize(e);
            }}
            placeholder="Type your message here... (Use markdown for formatting, Shift+Enter for new line)"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                sendMessage(e);
              }
            }}
            rows={1}
            style={{ minHeight: '38px', resize: 'vertical' }}
          />
          <button type="submit" disabled={!user || !newMessage.trim()}>
            Send
          </button>
        </form>
      </div>
    </div>
  );
};

export default Chat;
